import {Alert, Button, CircularProgress, MenuItem, Stack, Tooltip} from "@mui/material";
import React, {useState} from "react";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {ICompleteManualInterventionInput} from "../../models/CompleteManualInterventionInput";
import {Document} from "../../models/Document";
import {COMPLETE_MANUAL_INTERVENTION} from "../graphql/mutations/gqlCompleteManualIntervention";
import {translateFunction} from "../../../../localization/TranslationService";
import {IRunExtractorsInput} from "../../models/RunExtractorsInput";
import {RUN_EXTRACTORS} from "../graphql/mutations/gqlRunExtractors";
import {toast} from "react-toastify";
import {CallSplit, Comment, DriveFileMove, Engineering, Forward, Mail, Reply, Save} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import {DropdownMenu} from "../../../../components/common/DropdownMenu";
import {DocumentStatus} from "../../models/DocumentStatus";
import {SAVE_METADATA} from "../graphql/mutations/gqlSaveMetaData";

const translate = translateFunction("SureBaseDocuments");

export enum mailDocumentType
{
	Reply,
	Forward
}

interface IDocumentActionsCardProps {
	document: Document;
	documentIsValid: boolean;
	readOnly : boolean;
	locked: boolean;
	viewer?: boolean;
	reindexMode?: boolean;
	reloadDocument?: () => void;
	mailDocument?: (mailDocumentType: mailDocumentType) => void;
	splitDocument?: () => void;
	editComment?: () => void;
	reindex?:() => void;
	onClose?: () => void;
}

export const DocumentActionsCard: React.FC<IDocumentActionsCardProps> = (props) => {
	const navigate = useNavigate();
	const [submitLoading, setSubmitLoading] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string>();
	const [navigateTo, setNavigateTo] = useState<string>();
	const approveDocument = async () => {
		const input: ICompleteManualInterventionInput = {
			documentId: props.document.id,
			success: true,
			taskToken: props.document.taskToken ?? "",
		};
		setSubmitLoading(true);
		setErrorMessage("");
		graphQLServices.documentsClient.mutate({
			mutation: SAVE_METADATA,
			variables: {
				input: {
					documentId: props.document.id,
					metaData: props.document.metaData,
					audits: props.document.audits
				}
			},
		})
			.then(() =>
				graphQLServices.documentsClient.mutate({
					mutation: COMPLETE_MANUAL_INTERVENTION,
					variables: {input: input},
				}))
			.then(() => {
				toast.success(translate(`Done.`));
				setNavigateTo("/documents");
			})
			.catch((reason) => setErrorMessage(reason.networkError.result.errors[0].extensions.message ?? reason.networkError.result.errors[0].message))
			.finally(() => {
				setSubmitLoading(false);
			}
			);

	};



	const saveMetaData = async() => {
		setSubmitLoading(true);
		setErrorMessage(undefined);
		graphQLServices.documentsClient.mutate({
			mutation: SAVE_METADATA,
			variables: {
				input: {
					documentId: props.document.id,
					metaData: props.document.metaData,
					audits: props.document.audits
				}
			},
		})
			.then(() => {
				toast.success(translate(`Saved.`));
			})
			.catch((reason) => {
				toast.error(`Save failed.`);
				setErrorMessage(reason.networkError.result.errors[0].extensions.message);
			} )
			.finally(() => {
				setSubmitLoading(false);

			});

	};
	const extractDocument = async () => {
		const input: IRunExtractorsInput = {
			documentId: props.document.id,
		};
		setSubmitLoading(true);
		setErrorMessage(undefined);
		graphQLServices.documentsClient.mutate({
			mutation: RUN_EXTRACTORS,
			variables: { input: input },
		})
			.then(() => {
				toast.success(translate(`Extraction done.`));
				if (props.reloadDocument)
					props.reloadDocument();
			})
			.catch((reason) => {
				toast.error(`Extraction failed.`);
				setErrorMessage(reason.networkError.result.errors[0].extensions.message);
			} )
			.finally(() => {
				setSubmitLoading(false);

			});
	};

	const mailDocument = (mailDocumentType: mailDocumentType) => {
		if (props.mailDocument) {
			props.mailDocument(mailDocumentType);
		}
	};

	const reindex = () => {
		if (props.reindex)
			props.reindex();
	};

	const splitDocument = () => {
		if (props.splitDocument)
			props.splitDocument();
	};

	const editComment = () => {
		if (props.editComment)
			props.editComment();
	};

	if (submitLoading) {
		return (
			<CircularProgress></CircularProgress>
		);
	}

	if (navigateTo) {
		navigate(navigateTo);
	}
	return (
		<Stack direction={"row"} spacing={1}>
			{errorMessage &&
				<Alert severity={"error"}>{JSON.stringify(errorMessage)}</Alert>
			}

			{props.document && <>
				{props.document && !props.readOnly &&
				<>
					{!props.reindexMode	&& <>
						<Button variant={"contained"} onClick={approveDocument} disabled={!props.documentIsValid}>{translate("Approve")}</Button>
						<Tooltip title={"Save"}><Button variant={"outlined"} onClick={saveMetaData}><Save/></Button></Tooltip>
					</>
					}
					{props.reindexMode && <Button variant={"contained"} onClick={props.reindex} disabled={!props.documentIsValid}>{translate("Reindex")}</Button>}

					{props.document.status === DocumentStatus.Manual && <Tooltip title={"Run Extract"}><Button variant={"outlined"} onClick={extractDocument} ><Engineering/></Button></Tooltip>}
					{props.splitDocument && <Tooltip title={"Split document"}><Button variant={"outlined"} onClick={splitDocument}><CallSplit/></Button></Tooltip> }
				</>
				}

				{props.document && props.mailDocument &&
					<>
						<DropdownMenu
							label={<Tooltip title={"Mail document"}><Mail/></Tooltip>}
							variant={"outlined"}>
							<MenuItem onClick={() => mailDocument(mailDocumentType.Reply)}><Reply/>Reply</MenuItem>
							<MenuItem onClick={() => mailDocument(mailDocumentType.Forward)}><Forward/>Forward</MenuItem>
						</DropdownMenu>
					</>
				}
				{!props.reindexMode && props.document && props.document.status > DocumentStatus.Manual &&
					<Tooltip title={translate("Reindex")}><Button variant={"outlined"} onClick={reindex} ><DriveFileMove/></Button></Tooltip>
				}
				{(!props.readOnly && (!props.document.comment || props.document.comment  === "")) &&
					<Tooltip title={translate("Edit comment")}><Button variant={"outlined"} onClick={editComment} ><Comment/></Button></Tooltip>
				}
				{(props.viewer && props.readOnly && props.document.comment && props.document.comment  !== "") &&
					<Tooltip title={props.document.comment}><Button variant={"outlined"} onClick={editComment} ><Comment/></Button></Tooltip>
				}
			</>
			}
		</Stack>
	);
};
